import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Fingerprint } from 'lucide-react';
import { signOut, getCurrentUser } from '../../services/firebase';
import { cn } from '../../lib/utils';

export function PublicHeader() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    setMenuOpen(false);
    navigate('/', { replace: true });
  };

  return (
    <header className="pointer-events-none fixed top-0 left-0 right-0 p-4 md:p-5 z-50">
      <div
        className={cn(
          'pointer-events-auto relative mx-auto overflow-hidden transition-all duration-300 ease-in-out',
          'border border-slate-200 bg-white/90 backdrop-blur-md shadow-sm',
          menuOpen ? 'rounded-[20px]' : 'rounded-[999px]'
        )}
        style={{
          width: menuOpen
            ? 'min(90vw, 560px)'
            : user
            ? 'min(100%, 480px)'
            : 'min(100%, 320px)',
          height: menuOpen ? 'auto' : '60px',
        }}
      >
        {/* Compact bar */}
        <div className="flex items-center justify-between px-4 h-[60px]">

          {/* Logo */}
          <button
            onClick={() => { navigate('/'); setMenuOpen(false); }}
            className="flex items-center gap-2 group"
          >
            <Fingerprint
              size={18}
              className="text-slate-900 group-hover:opacity-60 transition-opacity"
            />
            <span className="font-bold text-[17px] tracking-tight text-slate-900 group-hover:opacity-60 transition-opacity">
              Buildlog
            </span>
          </button>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {user && (
              <div className="hidden md:flex items-center gap-2 mr-1">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-full hover:bg-slate-200 transition-colors text-sm font-medium text-slate-800"
                >
                  <div className="w-5 h-5 rounded-full bg-brand-orange flex items-center justify-center text-[9px] font-bold text-black">
                    {user.displayName?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <span className="max-w-[100px] truncate">{user.displayName ?? 'Account'}</span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-colors"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={cn(
                'p-2 rounded-full transition-all duration-150',
                menuOpen
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-800 hover:bg-slate-100'
              )}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Expanded nav */}
        {menuOpen && (
          <div className="px-6 pb-6 pt-2 border-t border-slate-100 animate-in fade-in duration-150">
            <nav className="flex flex-col gap-1">
              {user ? (
                <>
                  {[
                    { label: 'Projects', path: '/dashboard' },
                    { label: 'Content', path: '/content' },
                    { label: 'Strategy', path: '/strategy' },
                    { label: 'Settings', path: '/settings' },
                  ].map(({ label, path }) => (
                    <button
                      key={path}
                      onClick={() => { navigate(path); setMenuOpen(false); }}
                      className="text-left text-2xl font-semibold text-slate-900 py-2 hover:text-slate-400 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    onClick={handleSignOut}
                    className="text-left text-2xl font-semibold text-red-400 py-2 hover:opacity-70 transition-opacity"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate('/'); setMenuOpen(false); }}
                    className="text-left text-2xl font-semibold text-slate-900 py-2 hover:text-slate-400 transition-colors"
                  >
                    Home
                  </button>
                  <button
                    onClick={() => { navigate('/login'); setMenuOpen(false); }}
                    className="text-left text-2xl font-semibold text-slate-900 py-2 hover:text-slate-400 transition-colors"
                  >
                    Sign in
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}